/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Document, Font, Image, Link, Page, StyleSheet,
  Text, View,
} from '@react-pdf/renderer';
import React from 'react';

import { formatPhoneDotsIntl, formatPhoneNumber, httpsRegex } from 'utils/lib';
import { montserratFont, vidalokaFont } from 'utils/pdf-fonts';

Font.register(vidalokaFont);

Font.register(montserratFont);

const styles = StyleSheet.create({
  page: {
    position: 'relative',
  },
  background: {
    position: 'absolute',
    top: -1, // -1 on all of these to fix off-by-1 errors with the background images
    left: -1,
    right: -1,
    bottom: -1,
  },
  agentPicture: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '0.9in',
    height: '1.15in',
    objectFit: 'cover',
    borderRadius: '12px',
  },
  teamOrCompanyLogo: {
    position: 'absolute',
    objectFit: 'contain',
    objectPosition: 'center bottom',
  },
  agentInfoText: {
    color: '#f1f0ea',
    position: 'absolute',
    top: '-0.02in',
    left: '1.15in',
  },
  link: {
    color: '#f1f0ea',
    textDecoration: 'none',
  },
});

const AgentInfo = ({ userInfo, top, left }: any) => (
  <View style={{
    position: 'absolute', top, left, right: 0,
  }}
  >
    <Image src={userInfo.agentInfo.agentPictureUrl} style={styles.agentPicture} />

    <View style={styles.agentInfoText}>
      <Text style={{
        fontFamily: 'Vidaloka',
        fontSize: '18px',
        textTransform: 'uppercase',
        marginBottom: '4px',
      }}
      >
        {`${userInfo.firstName} ${userInfo.lastName ?? ''}`}
      </Text>

      <Text style={{
        fontFamily: 'Montserrat',
        fontWeight: 700,
        fontSize: '8px',
        textTransform: 'uppercase',
      }}
      >
        {`Lic. # ${userInfo.agentInfo.agentDreNumber}`}
      </Text>

      <Text style={{
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: '14px',
        margin: '5px 0',
        letterSpacing: '1.2px',
      }}
      >
        <Link src={`tel:${formatPhoneNumber(userInfo.agentInfo.marketingPhoneNumber, true)}`} style={styles.link}>
          {formatPhoneDotsIntl(userInfo.agentInfo.marketingPhoneNumber)}
        </Link>
      </Text>

      <Text style={{
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontSize: '9px',
      }}
      >
        <Link src={`mailto:${userInfo.agentInfo.marketingEmail}`} style={styles.link}>
          {userInfo.agentInfo.marketingEmail}
        </Link>
      </Text>

      {userInfo.agentInfo.website && (
        <Text style={{
          fontFamily: 'Montserrat',
          fontWeight: 400,
          fontSize: '9px',
        }}
        >
          <Link src={userInfo.agentInfo.website} style={styles.link}>
            {userInfo.agentInfo.website.replace(httpsRegex, '')}
          </Link>
        </Text>
      )}
    </View>
  </View>
);

const TeamOrCompanyLogo = ({
  userInfo, top, left, preferLight,
}: any) => {
  // Init to normal (dark) logos, preferring the team logo over the company logo
  let imageUrl = userInfo.agentInfo.teamLogoUrl ?? userInfo.agentInfo.companyLogoUrl;

  if (preferLight) {
    // Use light logos, if they exist
    if (userInfo.agentInfo.teamLogoLightUrl) {
      imageUrl = userInfo.agentInfo.teamLogoLightUrl;
    } else if (userInfo.agentInfo.companyLogoLightUrl) {
      imageUrl = userInfo.agentInfo.companyLogoLightUrl;
    }
  }

  return (
    <Image
      src={imageUrl}
      style={{
        ...styles.teamOrCompanyLogo,
        top,
        left,
        width: '1.22in',
        height: '1.22in',
      }}
    />
  );
};

const SmartSellersDocument = ({ name, userInfo }: any) => (
  <Document title={name} author="Harvist">
    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/1.png" style={styles.background} />
      <AgentInfo userInfo={userInfo} top="9.24in" left="0.82in" />
      <TeamOrCompanyLogo userInfo={userInfo} top="9.24in" left="6.4in" preferLight />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/2.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/3.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/4.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/5.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/6.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/7.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/8.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/9.png" style={styles.background} />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>

    <Page size={{ width: '8.5in', height: '11in' }} style={styles.page}>
      <Image src="/free-resources/the-smart-sellers-playbook/10.png" style={styles.background} />
      <AgentInfo userInfo={userInfo} top="8.75in" left="0.82in" />
      <TeamOrCompanyLogo userInfo={userInfo} top="0.6in" left="6.4in" preferLight={false} />
    </Page>
  </Document>
);

export default SmartSellersDocument;
